// 手机号国家码（配合后端 utils/phone.js 的 E.164 规范化）。
// 用户全球分布，**禁止** zh-CN 硬编码 / /^1[3-9]\d{9}$/。
// value = ISO2（作为 country_code 传后端）；本地号在后端用 libphonenumber 规范为 E.164。
export const COUNTRY_CODES = [
  { code: 'CN', dial: '+86', label: '🇨🇳 +86 中国' },
  { code: 'JP', dial: '+81', label: '🇯🇵 +81 日本' },
  { code: 'US', dial: '+1', label: '🇺🇸 +1 美国' },
  { code: 'HK', dial: '+852', label: '🇭🇰 +852 香港' },
  { code: 'TW', dial: '+886', label: '🇹🇼 +886 台湾' },
  { code: 'KR', dial: '+82', label: '🇰🇷 +82 韩国' },
  { code: 'SG', dial: '+65', label: '🇸🇬 +65 新加坡' },
  { code: 'GB', dial: '+44', label: '🇬🇧 +44 英国' },
  { code: 'AU', dial: '+61', label: '🇦🇺 +61 澳大利亚' },
  { code: 'CA', dial: '+1', label: '🇨🇦 +1 加拿大' },
  { code: 'DE', dial: '+49', label: '🇩🇪 +49 德国' },
  { code: 'FR', dial: '+33', label: '🇫🇷 +33 法国' },
];

// 探测全部失败时的**最终**兜底。选 CN（历史用户主体在中国大陆）。
// 注意：这只是兜底——detectDefaultCountry() 会先按 CF-IPCountry / navigator.language 探测，
// 绝大多数访客不会走到这里。登录页因此默认不再「固定」中国。
export const DEFAULT_COUNTRY = 'CN';

// COUNTRY_CODES 里实际提供的 ISO2 集合——只把默认值设成「下拉里真有的国家」，
// 否则设了个选不到的默认反而更糟。
const SUPPORTED = new Set(COUNTRY_CODES.map((c) => c.code));

// 无 region 子标签时，按语言主标签粗映射到一个受支持国家（zh→CN、ja→JP…）。
const PRIMARY_LANG_TO_ISO2 = { zh: 'CN', ja: 'JP', ko: 'KR', en: 'US', de: 'DE', fr: 'FR' };

// 从浏览器语言（BCP-47，如 ja-JP / en-US / zh-Hans-CN）推地区码。
// 优先取末段 region（两位大写且受支持），否则按语言主标签兜。取不到返回 null。
export function countryFromNavigatorLanguage(nav = typeof navigator !== 'undefined' ? navigator : undefined) {
  if (!nav) return null;
  const tags = nav.languages && nav.languages.length ? nav.languages : (nav.language ? [nav.language] : []);
  for (const tag of tags) {
    if (!tag) continue;
    const parts = String(tag).split('-');
    const region = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
    if (/^[A-Z]{2}$/.test(region) && SUPPORTED.has(region)) return region;
    const byPrimary = PRIMARY_LANG_TO_ISO2[parts[0].toLowerCase()];
    if (byPrimary && SUPPORTED.has(byPrimary)) return byPrimary;
  }
  return null;
}

// 探测结果缓存：多个表单（Login/Register…）只探一次。
let cachedDefault = null;

/**
 * 探测默认手机区号（ISO2）。优先级：
 *   1) 后端 /api/geo/country（读 Cloudflare 注入的 CF-IPCountry）
 *   2) navigator.language 兜底
 *   3) DEFAULT_COUNTRY 最终兜底
 * 任何异常都安全回退，**绝不抛**（表单初值依赖它，不能让探测失败拖垮登录页）。
 * 只返回 COUNTRY_CODES 内真实存在的国家码。
 */
export async function detectDefaultCountry() {
  if (cachedDefault) return cachedDefault;
  try {
    // 延迟 require 避免 util 与 services/api 的加载顺序耦合；api baseURL 已含 /api
    const api = require('../services/api').default;
    const { data } = await api.get('/geo/country');
    const cc = data && typeof data.country === 'string' ? data.country.toUpperCase() : '';
    if (SUPPORTED.has(cc)) return (cachedDefault = cc);
  } catch (e) {
    /* 网络失败 / 接口未部署 / 本地无 CF 头 → 忽略，走下一档 */
  }
  const byLang = countryFromNavigatorLanguage();
  if (byLang) return (cachedDefault = byLang);
  return (cachedDefault = DEFAULT_COUNTRY);
}

// 仅供测试重置缓存用。
export function __resetDetectedCountryCache() {
  cachedDefault = null;
}

// antd Select 的 options（下拉显示全称，可搜索）
export const COUNTRY_SELECT_OPTIONS = COUNTRY_CODES.map((c) => ({
  value: c.code,
  label: c.label,
}));

// 非中文界面：国家名用 Intl.DisplayNames 按界面语言生成（清单里的 label 是中文），取不到时回落中文 label
export function countrySelectOptions(lang) {
  if (!lang || lang === 'zh-CN' || lang === 'zh') return COUNTRY_SELECT_OPTIONS;
  let names = null;
  try {
    names = new Intl.DisplayNames([lang], { type: 'region' });
  } catch (e) {
    return COUNTRY_SELECT_OPTIONS;
  }
  return COUNTRY_CODES.map((c) => {
    const flag = c.label.split(' ')[0];
    let name = null;
    try { name = names.of(c.code); } catch (e) { name = null; }
    return { value: c.code, label: name ? `${flag} ${c.dial} ${name}` : c.label };
  });
}

// 宽松的客户端手机格式校验（国家无关，仅挡明显乱输入；真正校验以后端 E.164 为准）。
export const LOOSE_PHONE_PATTERN = /^\+?[\d\s-]{5,20}$/;
